// clipApi.ts
import { APIFinalArgs, QueryResponse } from '@common/types/queryState';
import { apiPaths, baseApi } from '@services';

import { transformQueryResponse } from '@common/utils/';
import {
    BackendTrack,
    Clip,
    GetClipMediaArgs,
    GetClipsArgs,
    GetDirectClipArgs,
    GetSearchClipsResponse,
    GetWorkflowResponse,
    MediaIdentifier,
    Track,
    WorkflowEntry,
} from '../types';
import {
    mapBackendTracksToFrontend,
    mapDirectClipMediaToFrontend,
    mapDirectClipToFrontend,
    transformClip,
    transformWorkflow,
} from '../utils';

const extendedApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getClips: builder.query<
            QueryResponse<Clip>,
            APIFinalArgs<GetClipsArgs>
        >({
            query: ({ productionId, params }) => ({
                url: apiPaths.data.getClips(productionId),
                params,
            }),
            transformResponse: (response: GetSearchClipsResponse, meta, arg) =>
                transformQueryResponse(response, meta, arg, transformClip),

            // ({
            //     data: response.results.map(mapSearchClipToFrontend),
            //     totalCount: response.numFound,
            // }),
        }),

        getClip: builder.query<Clip, APIFinalArgs<GetDirectClipArgs>>({
            query: ({ productionId, assetId, params }) => ({
                url: apiPaths.data.getClip(productionId, assetId!),
                params,
            }),
            transformResponse: mapDirectClipToFrontend,
        }),

        getMediaObjectInstance: builder.query<
            MediaIdentifier,
            GetClipMediaArgs
        >({
            query: ({ productionId, mediaObjectId, moiId }) => ({
                url: apiPaths.media.getMoi(productionId, mediaObjectId, moiId),
            }),
            transformResponse: mapDirectClipMediaToFrontend,
        }),

        getClipTracks: builder.query<Track[], GetClipMediaArgs>({
            query: ({ productionId, mediaObjectId, moiId }) => ({
                url: apiPaths.media.getTracks(
                    productionId,
                    mediaObjectId,
                    moiId,
                ),
            }),
            transformResponse: (response: BackendTrack[]) =>
                response.map(mapBackendTracksToFrontend),
        }),

        getClipHistory: builder.query<
            QueryResponse<WorkflowEntry>,
            APIFinalArgs<GetClipsArgs>
        >({
            query: ({ productionId, params }) => ({
                url: apiPaths.data.getWorkflow(productionId),
                params,
            }),
            transformResponse: (response: GetWorkflowResponse, meta, arg) => {
                const totalResultsHeader = meta?.response?.headers.get(
                    'x-pagination-total-results',
                );
                const totalCount = parseInt(totalResultsHeader ?? '0', 10);

                const clips = transformWorkflow(response.results, meta, arg);
                const facet = response.facetFields;
                return {
                    data: clips,
                    totalCount,
                    facetFields: facet,
                };
            },
        }),
    }),
});

export const {
    useGetClipsQuery,
    useGetClipQuery,
    useGetMediaObjectInstanceQuery,
    useGetClipTracksQuery,
    useGetClipHistoryQuery,
} = extendedApi;

export default extendedApi.endpoints;
